import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDocs,
  getCountFromServer,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Project, Client } from '@/types';

/**
 * クライアント一覧を購読
 */
export const subscribeToClients = (
  userId: string,
  callback: (clients: Client[]) => void
) => {
  const q = query(
    collection(db, 'users', userId, 'clients'),
    orderBy('createdAt', 'desc')
  );

  return onSnapshot(q, (snapshot) => {
    const clients = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Client[];
    callback(clients);
  });
};
export const subscribeToProjects = (
  userId: string,
  status: 'active' | 'archived' | 'completed' | 'all',
  callback: (projects: Project[]) => void
) => {
  let q;
  
  if (status === 'all') {
    q = query(
      collection(db, 'users', userId, 'projects'),
      orderBy('createdAt', 'desc')
    );
  } else {
    q = query(
      collection(db, 'users', userId, 'projects'),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
  }

  return onSnapshot(
    q, 
    (snapshot) => {
      const projects = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Project[];
      callback(projects);
    },
    (error) => {
      console.error('Error fetching projects:', error);
      callback([]);
    }
  );
};

/**
 * プロジェクトを作成
 */
export const createProject = async (
  userId: string,
  projectData: Omit<Project, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const docRef = await addDoc(collection(db, 'users', userId, 'projects'), {
    ...projectData,
    userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
};

/**
 * プロジェクトを更新
 */
export const updateProject = async (
  userId: string,
  projectId: string,
  projectData: Partial<Project>
): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'projects', projectId);
  
  // undefined のフィールドを削除
  const cleanData: any = {};
  Object.keys(projectData).forEach((key) => {
    const value = (projectData as any)[key];
    if (value !== undefined) {
      cleanData[key] = value;
    }
  });
  
  await updateDoc(docRef, {
    ...cleanData,
    updatedAt: serverTimestamp(),
  });
};

/**
 * プロジェクトを削除
 */
export const deleteProject = async (
  userId: string,
  projectId: string
): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'projects', projectId);
  await deleteDoc(docRef);
};

/**
 * プロジェクトが使用中かチェック
 */
export const isProjectInUse = async (
  userId: string,
  projectId: string
): Promise<boolean> => {
  const q = query(
    collection(db, 'users', userId, 'timestamps'),
    where('projectId', '==', projectId),
    where('status', '==', 'active')
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
};

/**
 * クライアント一覧を取得
 */
export const getClients = async (userId: string): Promise<Client[]> => {
  const q = query(
    collection(db, 'users', userId, 'clients'),
    orderBy('name', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Client[];
};

/**
 * クライアントを作成
 */
export const createClient = async (
  userId: string,
  clientData: Omit<Client, 'id' | 'userId' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const docRef = await addDoc(collection(db, 'users', userId, 'clients'), {
    ...clientData,
    userId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
};

/**
 * クライアントを更新
 */
export const updateClient = async (
  userId: string,
  clientId: string,
  clientData: Partial<Client>
): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'clients', clientId);
  
  // undefined のフィールドを削除
  const cleanData: any = {};
  Object.keys(clientData).forEach((key) => {
    const value = (clientData as any)[key];
    if (value !== undefined) {
      cleanData[key] = value;
    }
  });
  
  await updateDoc(docRef, {
    ...cleanData,
    updatedAt: serverTimestamp(),
  });
};

/**
 * クライアントを削除
 */
export const deleteClient = async (
  userId: string,
  clientId: string
): Promise<void> => {
  const docRef = doc(db, 'users', userId, 'clients', clientId);
  await deleteDoc(docRef);
};
/**
 * 🆕 アクティブなプロジェクト数を取得
 */
export const getActiveProjectCount = async (userId: string): Promise<number> => {
  const q = query(
    collection(db, 'users', userId, 'projects'),
    where('status', '==', 'active')
  );
  
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
};
